/**
 * @sazeo/sdk — Quota limits + useQuotaStatus hook
 *
 * Computes whether the tenant is approaching or over the seat limit for
 * their current billing plan. Used by QuotaBanner in tenant-app.
 */

import { useQuery } from '@tanstack/react-query';
import { useSubscription } from './billing';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Plan limits
// ---------------------------------------------------------------------------

export const PLAN_LIMITS = {
  free: { users: 25, storage_bytes: 1_073_741_824, api_calls_per_day: 1_000 },
  pro: { users: 100, storage_bytes: 26_843_545_600, api_calls_per_day: 10_000 },
  enterprise: {
    users: Number.POSITIVE_INFINITY,
    storage_bytes: Number.POSITIVE_INFINITY,
    api_calls_per_day: Number.POSITIVE_INFINITY,
  },
} as const satisfies Record<string, Record<string, number>>;

export type Plan = keyof typeof PLAN_LIMITS;
/** Alias matching the task spec. */
export type PlanName = Plan;
export type QuotaResource = keyof (typeof PLAN_LIMITS)['free'];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface QuotaStatus {
  plan: Plan;
  users: { current: number; limit: number; pct: number };
  overLimit: boolean;
  nearLimit: boolean;
}

// ---------------------------------------------------------------------------
// Internal: count active members via Edge Function
// ---------------------------------------------------------------------------

async function fetchActiveMemberCount(supabase: SupabaseClientLike): Promise<number> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const headers: Record<string, string> = token ? { authorization: `Bearer ${token}` } : {};

  const res = await fetch('/functions/v1/people/members?status=active&limit=1', { headers });
  if (!res.ok) throw new Error(`member-count fetch failed: ${res.statusText}`);

  const body = (await res.json()) as { total?: number };
  return body.total ?? 0;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * useQuotaStatus — combines the subscription plan with the active member
 * count to derive whether the tenant is near (≥80%) or over (≥100%) their
 * seat limit.
 *
 * Enterprise plan always returns nearLimit=false, overLimit=false.
 * Returns null data while either query is loading.
 */
export function useQuotaStatus(
  client: SupabaseClientLike,
  tenantId: string,
): { data: QuotaStatus | null; isLoading: boolean; isError: boolean } {
  const subQuery = useSubscription(client, tenantId);

  const memberCountQuery = useQuery({
    queryKey: ['quota', 'member-count', tenantId],
    queryFn: () => fetchActiveMemberCount(client),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });

  const isLoading = subQuery.isLoading || memberCountQuery.isLoading;
  const isError = subQuery.isError || memberCountQuery.isError;

  if (isLoading || isError || !subQuery.data || memberCountQuery.data === undefined) {
    return { data: null, isLoading, isError };
  }

  const plan = (subQuery.data.plan ?? 'free') as Plan;

  if (plan === 'enterprise') {
    return {
      data: {
        plan,
        users: { current: memberCountQuery.data, limit: Number.POSITIVE_INFINITY, pct: 0 },
        overLimit: false,
        nearLimit: false,
      },
      isLoading: false,
      isError: false,
    };
  }

  const limit = PLAN_LIMITS[plan].users;
  const current = memberCountQuery.data;
  const pct = limit > 0 ? (current / limit) * 100 : 0;

  return {
    data: {
      plan,
      users: { current, limit, pct },
      overLimit: pct >= 100,
      nearLimit: pct >= 80,
    },
    isLoading: false,
    isError: false,
  };
}
